import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Package, FolderTree, Truck, FileText, 
  TrendingDown, TrendingUp, BarChart3, AlertTriangle 
} from 'lucide-react';
import './InventoryManagementModule.css';

// Import individual tab components
import ItemsTab from './tabs/ItemsTab';
import CategoriesTab from './tabs/CategoriesTab';
import VendorsTab from './tabs/VendorsTab';
import PurchaseOrdersTab from './tabs/PurchaseOrdersTab';
import StockTransactionsTab from './tabs/StockTransactionsTab';
import WastageTab from './tabs/WastageTab';
import ReportsTab from './tabs/ReportsTab';
import AlertsTab from './tabs/AlertsTab';

const InventoryManagementModule = () => {
  const { token, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('items');
  const [hotelId, setHotelId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Extract hotel_id from userProfile data - try multiple possible locations
    if (userProfile) {
      const extractedHotelId = userProfile.hotel_id || 
                               userProfile.hotelId || 
                               userProfile.managed_hotel_id ||
                               userProfile.managedHotels?.[0]?.hotel_id || 
                               null;
      
      console.log('[InventoryManagementModule] userProfile:', userProfile);
      console.log('[InventoryManagementModule] Extracted hotel_id:', extractedHotelId);
      console.log('[InventoryManagementModule] token exists:', !!token);
      
      setHotelId(extractedHotelId);
      setLoading(false);
    }
  }, [userProfile, token]);

  const tabs = [
    {
      id: 'items',
      label: 'Items',
      icon: Package,
      component: ItemsTab,
      description: 'Manage inventory items and stock levels'
    },
    {
      id: 'categories',
      label: 'Categories',
      icon: FolderTree,
      component: CategoriesTab,
      description: 'Organize items into categories'
    },
    {
      id: 'vendors',
      label: 'Vendors',
      icon: Truck,
      component: VendorsTab,
      description: 'Manage suppliers and vendor performance'
    },
    {
      id: 'purchase-orders',
      label: 'Purchase Orders',
      icon: FileText,
      component: PurchaseOrdersTab,
      description: 'Create and track purchase orders'
    },
    {
      id: 'stock-transactions',
      label: 'Stock Transactions',
      icon: TrendingUp,
      component: StockTransactionsTab,
      description: 'Track stock movements and adjustments'
    },
    {
      id: 'wastage',
      label: 'Wastage',
      icon: TrendingDown,
      component: WastageTab,
      description: 'Monitor and analyze wastage patterns'
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      component: ReportsTab,
      description: 'Generate inventory reports and analytics'
    },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: AlertTriangle,
      component: AlertsTab,
      description: 'Manage low stock and expiry alerts'
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  // Show loading if still determining hotelId
  if (loading) {
    return (
      <div className="inventory-management-loading">
        <div className="loading-spinner"></div>
        <p>Loading hotel information...</p>
      </div>
    );
  }

  // Show error if no hotelId found
  if (!hotelId) {
    return (
      <div className="inventory-management-module" style={{padding: '40px'}}>
        <div style={{textAlign: 'center', maxWidth: '600px', margin: '0 auto'}}>
          <h3 style={{color: '#ef4444', marginBottom: '16px'}}>Hotel ID Not Found</h3>
          <p style={{marginBottom: '20px'}}>Unable to determine your hotel. Please ensure you are assigned to a hotel.</p>
          <details style={{textAlign: 'left', background: '#f9fafb', padding: '16px', borderRadius: '8px', marginTop: '20px'}}>
            <summary style={{cursor: 'pointer', fontWeight: 600, marginBottom: '12px'}}>Debug: User Data</summary>
            <pre style={{fontSize: '12px', overflow: 'auto', whiteSpace: 'pre-wrap'}}>
              {JSON.stringify(userProfile, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-management-module">
      {/* Header with Module Title */}
      <div className="module-header">
        <div className="module-title">
          <Package className="module-icon" size={32} />
          <div>
            <h1>Inventory Management System</h1>
            <p className="module-subtitle">Comprehensive stock control and procurement center</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <div className="tabs-container">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                title={tab.description}
              >
                <Icon size={20} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active Tab Content */}
      <div className="tab-content">
        {ActiveComponent && (
          <ActiveComponent 
            hotelId={hotelId} 
            token={token}
            user={userProfile}
          />
        )}
      </div>
    </div>
  );
};

export default InventoryManagementModule;
